import React, { useCallback, useEffect, useState, useMemo } from 'react';
import GoogleMapReact from 'google-map-react';

import { Grid, Typography, Paper, IconButton, Button, TextField } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import PinDropIcon from '@mui/icons-material/PinDrop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import {
  mapOptions,
  BACKGROUND_COLOR,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  COLOR_ACCENT_1,
  COLOR_PRIMARY,
} from 'src/utils/const';
import { callApi } from 'src/core/callApi';
import { GOOGLE_MAPS_API_KEY } from 'src/utils/keys';

const RIPPLE_SIZE = 100;
const RIPPLE_COLOR = COLOR_ACCENT_1;

const useStyles = makeStyles((theme: Theme) => ({
  paperModal: {
    backgroundColor: BACKGROUND_COLOR,
    width: '100%',
    padding: theme.spacing(2),
    animation: 'fade-scale-in 0.3s ease forwards',
    display: 'flex',
    flexDirection: 'column',
  },
  tailoredRegionMapContainer: {
    height: '50vh',
    width: '100%',
  },
  tailoredRegionMapTopButtonContainer: {
    marginTop: -20,
    width: 'auto',
    flexWrap: 'nowrap',
  },
  button: {
    background: COLOR_PRIMARY_DARK,
    borderRadius: 5,
    border: 0,
    color: 'white',
    padding: '5px 30px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
    fontSize: 16,
  },
  ripple: {
    position: 'absolute',
    top: 0,
    left: 0,
    marginTop: -RIPPLE_SIZE / 2,
    marginLeft: -RIPPLE_SIZE / 2,
    width: RIPPLE_SIZE,
    height: RIPPLE_SIZE,
    animation: `$rippleAnimation 1000ms ${theme.transitions.easing.easeOut}`,
    animationIterationCount: 'infinite',
    opacity: 0,
    borderRadius: '50%',
    boxShadow: `0 0 6px 3px ${RIPPLE_COLOR}`,
  },
  '@keyframes rippleAnimation': {
    '0%': {
      transform: 'scale(0.1, 0.1)',
      opacity: 0,
    },
    '1%': {
      opacity: 1,
    },
    '50% ': {
      opacity: 1,
    },
    '100%': {
      transform: 'scale(1.2, 1.2)',
      opacity: 0,
    },
  },
}));

const initiateMapFromDrawing = (polygon, drawingManager) => {
  polygon && polygon.setMap(null);
  showDrawingTool(drawingManager);
};

const showDrawingTool = drawingManager => {
  drawingManager.setOptions({
    drawingControl: true,
  });
};

const removeDrawingTool = drawingManager => {
  drawingManager.setDrawingMode(null);
  drawingManager.setOptions({
    drawingControl: false,
  });
};

const getTailorRegionAllowed = (tailoredListings: number) => {
  if (tailoredListings < 10) {
    return false;
  }
  if (tailoredListings > 30000) {
    return false;
  }
  return true;
};

const handleTailorRegionCreation = (
  drawingManager,
  polygonArray,
  setDisableButton,
  setFinalPolygon
) => {
  removeDrawingTool(drawingManager);

  let polygonString = '';
  polygonArray.forEach((polygon, i) => {
    polygonString += `(${polygon.toString().replace(',', ' ')}),`;
  });
  polygonString = polygonString.slice(0, -1);

  setFinalPolygon(polygonString);
  setDisableButton(false);
};

const BigTailoredRegion = () => {
  const classes = useStyles();

  const [disableButton, setDisableButton] = useState(true);
  const [centerLat, setCenterLat] = useState(39.210878338011376);
  const [centerLng, setCenterLng] = useState(-101.51684523289367);
  const [homeLat, setHomeLat] = useState(39.210878338011376);
  const [homeLng, setHomeLng] = useState(-101.51684523289367);
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [drawingManagerCopy, setDrawingManagerCopy] = useState(null as any);
  const [polygonCopy, setPolygonCopy] = useState(null as any);

  const [marketName, setMarketName] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('');
  const [finalPolygon, setFinalPolygon] = useState<string>('');

  const [isBigRegionAPICalled, setIsBigRegionAPICalled] = useState<boolean>(false);

  const params = new URLSearchParams(window.location.search);

  const locationPin = useMemo(() => {
    return (
      <div>
        <div className={classes.ripple}></div>
        <PinDropIcon
          style={{
            fontSize: 40,
            position: 'absolute',
            top: -20,
            left: -20,
          }}
        />
      </div>
    );
  }, [centerLat, centerLng]);

  const tailoredRegionApiIsLoaded = (map, maps) => {
    const drawingManager = new maps.drawing.DrawingManager({
      drawingControl: true,
      drawingControlOptions: {
        position: maps.ControlPosition.TOP_LEFT,
        drawingModes: [maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        editable: true,
        draggable: false,
      },
    });
    setDrawingManagerCopy(drawingManager);
    drawingManager.setMap(map);

    maps.event.addListener(drawingManager, 'polygoncomplete', polygon => {
      setPolygonCopy(polygon);
      let polygonArray: number[][] = [];
      let polygonPaths = polygon.getPath().getArray();
      console.log(polygonPaths);
      polygonPaths.forEach(path => {
        polygonArray.push([path.lat(), path.lng()]);
      });
      handleTailorRegionCreation(drawingManager, polygonArray, setDisableButton, setFinalPolygon);

      maps.event.addListener(polygon.getPath(), 'set_at', () => {
        let polygonArray: number[][] = [];
        let polygonPaths = polygon.getPath().getArray();
        polygonPaths.forEach(path => {
          polygonArray.push([path.lat(), path.lng()]);
        });
        handleTailorRegionCreation(drawingManager, polygonArray, setDisableButton, setFinalPolygon);
      });

      maps.event.addListener(polygon.getPath(), 'insert_at', () => {
        let polygonArray: number[][] = [];
        let polygonPaths = polygon.getPath().getArray();
        polygonPaths.forEach(path => {
          polygonArray.push([path.lat(), path.lng()]);
        });
        handleTailorRegionCreation(drawingManager, polygonArray, setDisableButton, setFinalPolygon);
      });
    });
  };

  const handleTailoredRegionMapChange = useCallback(({ center }) => {
    const centerLat = center.lat();
    const centerLng = center.lng();
    setCenterLat(centerLat);
    setCenterLng(centerLng);
  }, []);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      position => {
        setHomeLat(position.coords.latitude);
        setHomeLng(position.coords.longitude);
        setCenterLat(position.coords.latitude);
        setCenterLng(position.coords.longitude);
        setLocationAllowed(true);
      },
      error => {
        console.error('Error Code = ' + error.code + ' - ' + error.message);
      }
    );
  }, []);

  const setCustomMarket = async () => {
    const createTailorRegionConfig = {
      queryParams: {
        polygon: finalPolygon,
        countryCode: countryCode,
        marketName: marketName,
      },
    };

    const apiURL =
      'https://ddcagqvs82.execute-api.us-east-2.amazonaws.com/default/createBigMarketTailorRegionProxy';
    console.log('>>', createTailorRegionConfig);
    setIsBigRegionAPICalled(true);
    callApi(apiURL, createTailorRegionConfig, true);
  };

  const resetTailoredRegion = () => {
    initiateMapFromDrawing(polygonCopy, drawingManagerCopy);
  };

  return (
    <>
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh', width: '100%', padding: '40px', background: BACKGROUND_COLOR }}
      >
        <div className={`${classes.paperModal} modal-custom`}>
          {isBigRegionAPICalled && (
            <div
              style={{
                height: '90vh',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography>Your Big Market is under build !</Typography>
            </div>
          )}
          {!isBigRegionAPICalled && (
            <>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                style={{ marginBottom: 0, flexWrap: 'nowrap', width: 'auto' }}
              >
                <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                  <Typography variant="h5">Select Location</Typography>
                </Grid>
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-end"
                  className={classes.tailoredRegionMapTopButtonContainer}
                >
                  <IconButton>
                    <RestartAltIcon
                      style={{
                        color: COLOR_PRIMARY,
                        fontSize: 40,
                      }}
                      onClick={resetTailoredRegion}
                    />
                  </IconButton>
                </Grid>
              </Grid>

              <Paper className={classes.tailoredRegionMapContainer}>
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: GOOGLE_MAPS_API_KEY,
                    libraries: ['drawing', 'places', 'visualization'],
                    id: 'onboarding-modal-map',
                  }}
                  center={
                    locationAllowed
                      ? { lat: homeLat, lng: homeLng }
                      : params.has('lat') &&
                        params.has('lng') &&
                        !isNaN(Number(params.get('lat'))) &&
                        !isNaN(Number(params.get('lng')))
                      ? { lat: Number(params.get('lat')), lng: Number(params.get('lng')) }
                      : { lat: 39.210878338011376, lng: -101.51684523289367 }
                  }
                  options={mapOptions}
                  zoom={locationAllowed ? 6 : 2}
                  resetBoundsOnResize
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) => {
                    tailoredRegionApiIsLoaded(map, maps);
                  }}
                  onDragEnd={map => handleTailoredRegionMapChange(map)}
                >
                  {locationPin}
                </GoogleMapReact>
              </Paper>

              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginTop: 10, marginBottom: 10 }}
                spacing={4}
              >
                <Grid item sm={6}>
                  <TextField
                    fullWidth
                    label="Country Code (eg. US,UK,FR)"
                    variant="filled"
                    onChange={e => setCountryCode(e.target.value)}
                  />
                </Grid>
                <Grid item sm={6}>
                  <TextField
                    fullWidth
                    label="Market Name"
                    variant="filled"
                    onChange={e => setMarketName(e.target.value)}
                  />
                </Grid>
              </Grid>
              <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.button}
                  onClick={setCustomMarket}
                  disabled={disableButton}
                >
                  Set your own market
                </Button>
              </Grid>
            </>
          )}
        </div>
      </Grid>
    </>
  );
};

export default BigTailoredRegion;
